import PropTypes from 'prop-types';
import { Fragment } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import Ad from '../Ad/Ad';
import { AdSizes } from '../Ad/AdSizes';

import ArticleFeature from '../article/ArticleFeature';
import ArticleAffiliate from '../article/ArticleAffiliate';
import ArticleTags from '../article/ArticleTags';
import RecipeRatings from './RateRecipe/RecipeRatings';
import CorusImage from '../CorusImage/CorusImage';
import Newsletter from '../Newsletter/Newsletter';

import JsonRecipeContent from './JsonRecipeContent';

import htmlToReact from '../../utils/htmlToReact';
import { NS_RECIPE } from '../../utils/lang';
import {
	createPostContent, findAndReplaceInString, isFrenchPage, isValidRecipeQuantity,
} from '../../utils/utility';

const adSizes = {
	'xs sm': AdSizes.BIG_BOX,
	md: AdSizes.LEADERBOARD_BIGBOX_COMBO,
	lg: AdSizes.BIG_BOX,
	'xl xxl': AdSizes.LEADERBOARD_BIGBOX_COMBO,
};

function RecipeDetailsItem({ title, value }) {
	return (
		<div className="recipe-details-item">
			<div className="recipe-details-item-title">{title}</div>
			<div className="recipe-details-item-value">
				{value}
			</div>
		</div>
	);
}

RecipeDetailsItem.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
};

function IngredientSubstitionsGuideLink() {
	return (
		<div className="ingredient-substitution-link">
			<Link
				href="/ingredient-substitutions/"
				target="_blank"
				rel="noopener noreferrer"
			>
				Ingredient Substitution Guide
			</Link>
		</div>
	);
}

function RecipeNote({ title, note }) {
	return (
		<div className="recipe-notes">
			<div className="recipe-notes-title">{title}</div>
			<p className="recipe-notes-text">{note}</p>
		</div>
	);
}

RecipeNote.propTypes = {
	title: PropTypes.string.isRequired,
	note: PropTypes.string.isRequired,
};

function RecipeDirections({ steps }) {
	const { t } = useTranslation(NS_RECIPE);

	return (
		<>
			{steps.map((step, index) => (
				<Fragment key={step.recipeDetailPreparationStep}>
					<div className="recipe-step">
						<div id={`step${index + 1}`} className="recipe-step-title">
							{`${t('step')} ${index + 1}`}
						</div>

						<div className="recipe-step-content">
							{htmlToReact(createPostContent(step.recipeDetailPreparationStep))}
						</div>

						{step.recipeDetailPreparationImage
							? (
								<div className="recipe-step-image">
									<CorusImage
										src={step.recipeDetailPreparationImage?.sourceUrl}
										alt={step.recipeDetailPreparationImage?.altText}
										title={step.recipeDetailPreparationImage?.title}
										className="recipe-step-image-file"
										height={parseInt(step.recipeDetailPreparationImage?.mediaDetails.height, 10)}
										width={parseInt(step.recipeDetailPreparationImage?.mediaDetails.width, 10)}
									/>
								</div>
							) : null}
					</div>
				</Fragment>
			))}
		</>
	);
}

RecipeDirections.propTypes = {
	steps: PropTypes.array.isRequired,
};

function SubRecipeIngredients({ subRecipe = {} }) {
	return (
		<>
			{subRecipe?.subRecipeIngredientRepeater?.map((ingredient) => {
				if (ingredient.subRecipeIngredientName == null) {
					return null;
				}

				const words = ingredient.subRecipeIngredientName.trim().split(' ');

				if (words && !words.length) {
					return null;
				}

				let ingredientQuantity = '';
				let ingredientName = '';
				let i = 0;

				// Ingredients need to be separated into quantity and name for layout
				for (i = 0; i < words.length; i += 1) {
					if (isValidRecipeQuantity(words[i])) {
						if (i > 0) {
							ingredientQuantity += ' ';
						}
						ingredientQuantity += words[i];
					} else {
						ingredientName = words.slice(i).join(' ');
						break;
					}
				}

				return (
					<Fragment key={ingredient.subRecipeIngredientName}>
						<div className="subrecipe-ingredients--quantity">{ingredientQuantity}</div>
						<div className="subrecipe-ingredients--name">{ingredientName}</div>
					</Fragment>
				);
			})}
		</>
	);
}

SubRecipeIngredients.propTypes = {
	subRecipe: PropTypes.object,
};

function Ingredients({ subRecipes }) {
	return (
		<>
			{subRecipes.map((subRecipe) => (
				<div className="subrecipe" key={subRecipe.subRecipeName}>
					{!subRecipe.subRecipeName || subRecipes.length === 1
						? null
						: (
							<h3 className="subrecipe-title">
								{subRecipe.subRecipeName}
							</h3>
						)}
					<div className="subrecipe-ingredients">
						<SubRecipeIngredients subRecipe={subRecipe} />
					</div>
				</div>
			))}
		</>
	);
}

Ingredients.propTypes = {
	subRecipes: PropTypes.array.isRequired,
};

export default function SingleRecipe({ recipe }) {
	const { t, i18n } = useTranslation(NS_RECIPE);
	const currentLanguage = i18n.language;

	const jsonRecipeContent = new JsonRecipeContent(recipe);
	const { recipeDetails } = recipe;
	const preparationSteps = recipeDetails.recipeDetailPreparationStepRepeater ?? [];
	const subRecipes = recipe.subRecipes.subRecipeRepeater ?? [];
	const directionsImage = recipeDetails.recipeDetailPreparationImage;
	const note = recipeDetails.recipeDetailNote;

	const prepTime = jsonRecipeContent.getPrepTime() ? jsonRecipeContent.convertTimeToHoursAndMin(jsonRecipeContent.getPrepTime()) : null;
	const cookTime = jsonRecipeContent.getCookTime() ? jsonRecipeContent.convertTimeToHoursAndMin(jsonRecipeContent.getCookTime()) : null;
	const yields = jsonRecipeContent.getYield() ? findAndReplaceInString(jsonRecipeContent.getYield(), '"', '') : null;

	const recipeMeta = [
		{
			titleKey: 'prepTime',
			value: prepTime,
		},
		{
			titleKey: 'cookTime',
			value: cookTime,
		},
		{
			titleKey: 'yields',
			value: yields,
		},
	];

	return (
		<div className="SingleRecipe article-body">
			<ArticleFeature
				content={recipe}
			/>
			<div className="recipe-details">
				{recipeMeta.map(({ titleKey, value }) => (value ? (
					<Fragment key={titleKey}>
						<RecipeDetailsItem title={t(titleKey)} value={value} />
					</Fragment>
				) : <></>))}
			</div>
			<div className="recipe-description">{htmlToReact(recipe)}</div>
			<Ad
				sizes={adSizes}
				htmlId="recipe-details-ad"
				wrapperClass="recipe-details-ad"
				className="recipe-description-ad-wrapper"
				pos="2"
			/>
			<div className="recipe-ingredients">
				<h2 className="recipe-ingredients-title">{t('ingredients')}</h2>
				<Ingredients subRecipes={subRecipes} />
				{!isFrenchPage(currentLanguage) && <IngredientSubstitionsGuideLink />}
			</div>
			<Ad
				sizes={adSizes}
				htmlId="recipe-ingredients-ad"
				wrapperClass="recipe-ingredients-ad"
				className="recipe-ingredients-ad-wrapper"
				pos="3"
			/>
			<div className="recipe-directions">
				<h2 className="recipe-directions-title">{t('directions')}</h2>
				{note && <RecipeNote title={t('notes')} note={note} />}
				{directionsImage && (
					<div className="recipe-directions-image">
						<CorusImage
							src={directionsImage?.sourceUrl}
							alt={directionsImage?.altText}
							title={directionsImage?.title}
							className="recipe-directions-image-file"
							height={parseInt(directionsImage?.mediaDetails.height, 10)}
							width={parseInt(directionsImage?.mediaDetails.width, 10)}
						/>
					</div>
				)}
				<RecipeDirections steps={preparationSteps} />
			</div>
			{!isFrenchPage(currentLanguage) && <ArticleTags article={recipe} />}
			<RecipeRatings recipe={jsonRecipeContent} />
			<Newsletter size="boxed" type="recipe" />
			<ArticleAffiliate article={recipe} />
			<div className="ad-wrapper recipe-bottom-ad-wrapper">
				<Ad
					sizes={adSizes}
					htmlId="recipe-bottom-ad"
					wrapperClass="recipe-bottom-ad"
					className="recipe-bottom-ad-wrapper"
					pos="4"
				/>
			</div>
		</div>
	);
}

SingleRecipe.propTypes = {
	recipe: PropTypes.object.isRequired,
};
